// src/clock/JobFenceAuto.js
//
// Automatic check-in / check-out from job fences, for the web app.
//
//   const fenceAuto = useJobFenceAuto({ clock, jobVisits, jobs: myJobs });
//   <FenceAutoNotice fenceAuto={fenceAuto} />
//
// While the person is clocked in and the app is open on screen, this reads
// the phone's location every FENCE_POLL_MS and hands it to
// decideFenceAction() (jobFence.js), which holds all the rules. Only jobs
// with a CONFIRMED fence take part; every other job behaves as before.
//
// Web app limit: browsers stop giving location when the app is closed or the
// screen is locked. If someone leaves while the app is closed, they are
// checked out the next time the app is opened (or at Clock Out, as before),
// with the time they were last seen on site, marked as estimated. A native
// app can do this in the background; only this file's GPS source changes.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { silentlyBlockedByViewAs } from "../utils/viewAs";
import { db } from "../firebaseConfig";
import { getLocation } from "./useClockInLogic";
import {
  checkOutVisit,
  getCheckInJob,
  getVisitOccurrenceKey,
  isJobDoneForCheckIn,
  jobRunsToday,
  toDateSafe,
} from "./jobVisits";
import {
  FENCE_POLL_MS,
  decideFenceAction,
  emptyFenceState,
  getJobFence,
  isFenceActive,
} from "./jobFence";
import { useLanguage } from "../i18n";

// How often "last seen on site" is saved on the open visit while inside.
const SEEN_INSIDE_WRITE_MS = 5 * 60 * 1000;

const keyFor = (job) => `${job.id}_${getVisitOccurrenceKey(job)}`;

export function useJobFenceAuto({ clock, jobVisits, jobs = [] }) {
  const [lastEvent, setLastEvent] = useState(null); // {type, siteName, at, estimated}
  const [watching, setWatching] = useState(false);

  // Today's jobs with an active fence, one entry per job occurrence.
  const fencedToday = useMemo(() => {
    const seen = new Set();
    const out = [];
    (jobs || []).forEach((raw) => {
      const job = getCheckInJob(raw);
      if (!job?.id || !isFenceActive(job)) return;
      if (!jobRunsToday(job) || isJobDoneForCheckIn(job)) return;
      const key = keyFor(job);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ key, job, fence: getJobFence(job) });
    });
    return out;
  }, [jobs]);

  const openVisit = jobVisits?.openVisit || null;
  const openHasFence = !!getJobFence(openVisit?.fence);
  // Off entirely during "View as": the Admin's phone must never check the
  // viewed person in or out.
  const enabled =
    !silentlyBlockedByViewAs() &&
    !!clock?.isClockedIn && !!jobVisits?.ready && (fencedToday.length > 0 || openHasFence);

  // Refs so the timer always sees current values without restarting.
  const live = useRef({});
  live.current = { clock, jobVisits, fencedToday };
  const stateRef = useRef(emptyFenceState());
  const runningRef = useRef(false);
  const lastSeenWriteRef = useRef({});

  useEffect(() => {
    if (!enabled) {
      setWatching(false);
      return undefined;
    }
    setWatching(true);

    const tick = async () => {
      if (runningRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      const { clock: c, jobVisits: jv, fencedToday: fenced } = live.current;
      if (!c?.isClockedIn || !jv?.ready || jv.isBusy) return;

      runningRef.current = true;
      try {
        let gps;
        try {
          gps = await getLocation();
        } catch {
          return; // no reading this time; the clock panel already shows GPS errors
        }

        const open = jv.openVisit || null;
        const today = new Date().toDateString();
        const pastVisits = (jv.todaysVisits || [])
          .filter((v) => v.status === "checked_out")
          .map((v) => ({
            jobKey: `${v.jobId}_${v.occurrenceDate || ""}`,
            checkOutAtMs: toDateSafe(v.checkOutAt)?.getTime() || 0,
          }))
          .filter((v) => v.checkOutAtMs && new Date(v.checkOutAtMs).toDateString() === today);

        const now = Date.now();
        const result = decideFenceAction({
          point: gps,
          now,
          jobs: fenced,
          openVisit: open,
          openVisitJobKey: open ? `${open.jobId}_${open.occurrenceDate || ""}` : null,
          pastVisits,
          state: stateRef.current,
        });
        stateRef.current = result.state;

        if (result.action === "check_in") {
          const ok = await jv.checkIn(result.job, { auto: true, confirmed: true, gps });
          if (ok) {
            setLastEvent({ type: "in", siteName: result.job.siteName || "job", at: new Date() });
          }
        } else if (result.action === "check_out") {
          await checkOutVisit(result.visit, "left_fence", gps, {
            checkOutAt: new Date(result.at),
            fields: { checkOutTimeEstimated: !!result.estimated },
          });
          setLastEvent({
            type: "out",
            siteName: result.visit.siteName || "job",
            at: new Date(result.at),
            estimated: !!result.estimated,
          });
        } else if (result.inside && open?.id) {
          // Save "last seen on site" now and then, so a check-out after the
          // app was closed can use a real time.
          const last = lastSeenWriteRef.current[open.id] || 0;
          if (now - last > SEEN_INSIDE_WRITE_MS) {
            lastSeenWriteRef.current[open.id] = now;
            updateDoc(doc(db, "job_visits", open.id), { lastSeenInsideAt: new Date(now) }).catch(
              (e) => console.warn("lastSeenInsideAt write failed:", e)
            );
          }
        }
      } catch (err) {
        console.warn("Job fence check failed:", err);
      } finally {
        runningRef.current = false;
      }
    };

    tick();
    const id = setInterval(tick, FENCE_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);

  // Clocking out resets memory for the next day.
  useEffect(() => {
    if (!clock?.isClockedIn) stateRef.current = emptyFenceState();
  }, [clock?.isClockedIn]);

  return {
    watching,
    fencedJobCount: fencedToday.length,
    lastEvent,
    dismissEvent: () => setLastEvent(null),
  };
}

// ---------------------------------------------------------------------------
// Small strip under the check-in banner on Home.
export const FenceAutoNotice = ({ fenceAuto }) => {
  const { t } = useLanguage();
  const event = fenceAuto?.lastEvent;

  // Hide the "just happened" message after a minute.
  useEffect(() => {
    if (!event) return undefined;
    const id = setTimeout(() => fenceAuto.dismissEvent(), 60 * 1000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  if (!fenceAuto) return null;
  if (!fenceAuto.watching && !event) return null;

  const time = (d) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <div className="jci-fence-strip">
      {event ? (
        <span className={`jci-fence-event ${event.type}`}>
          {event.type === "in"
            ? `✓ ${t("Checked in automatically at {site} ({time})", { site: event.siteName, time: time(event.at) })}`
            : `✓ ${t("Checked out of {site} automatically at {time}", { site: event.siteName, time: time(event.at) })}${
                event.estimated ? ` ${t("(estimated, the app was closed when you left)")}` : ""
              }`}
        </span>
      ) : (
        <span>
          📍{" "}
          {fenceAuto.fencedJobCount === 1
            ? t("Auto check-in is on for 1 job today. Keep the app open when you arrive.")
            : fenceAuto.fencedJobCount
            ? t("Auto check-in is on for {n} jobs today. Keep the app open when you arrive.", { n: fenceAuto.fencedJobCount })
            : t("Auto check-in is on for your jobs today. Keep the app open when you arrive.")}
        </span>
      )}
    </div>
  );
};
